import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFrontElevation } from "./useHook";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import { Trash2, Upload } from "lucide-react";

const FrontElevationDetails = () => {
  const { getFrontElevationById, updateFrontElevation } = useFrontElevation();
  const [elevationData, setElevationData] = useState(null);
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("description");
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    plot_dimension: "",
    covered_area: "",
    floors: 0,
    bed_rooms: 0,
    bath_rooms: 0,
    associated_elevation: "",
    description: "",
    servant_quarters: false,
    terrace: false,
    car_parking: false,
    images: [],
    price: 0,
    customUrl: "",
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  useEffect(() => {
    if (id) {
      getFrontElevationById(id, setElevationData);
    }
  }, [id]);

  useEffect(() => {
    if (elevationData) {
      setFormData({
        title: elevationData.title || "",
        plot_dimension: elevationData.plot_dimension || "",
        covered_area: elevationData.covered_area || "",
        floors: elevationData.floors || 0,
        bed_rooms: elevationData.bed_rooms || 0,
        bath_rooms: elevationData.bath_rooms || 0,
        associated_elevation: elevationData.associated_elevation || "",
        description: elevationData.description || "",
        servant_quarters: elevationData.servant_quarters || false,
        terrace: elevationData.terrace || false,
        car_parking: elevationData.car_parking || false,
        images: [],
        price: elevationData.price || 0,
        customUrl: elevationData.customUrl || "",
      });
      setExistingImages(elevationData.images || []);
    }
  }, [elevationData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleQuillChange = (value) => {
    setFormData({
      ...formData,
      description: value,
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = files.map((file) => URL.createObjectURL(file));

    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));

    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index, isExisting = false) => {
    if (isExisting) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newImages = [...formData.images];
      const newPreviews = [...imagePreviews];

      URL.revokeObjectURL(newPreviews[index]);
      newImages.splice(index, 1);
      newPreviews.splice(index, 1);

      setFormData((prev) => ({
        ...prev,
        images: newImages,
      }));
      setImagePreviews(newPreviews);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "images") {
          apiData.append(key, formData[key]);
        }
      });

      // Add new images
      formData.images.forEach((image) => {
        apiData.append("images", image);
      });

      // Add existing images that weren't removed
      existingImages.forEach((image) => {
        apiData.append("existing_images", JSON.stringify(image));
      });

      await updateFrontElevation(id, apiData);
      await getFrontElevationById(id, setElevationData);
      setShowEditModal(false);

      // Cleanup image previews
      imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
      setImagePreviews([]);

      Swal.fire({
        icon: "success",
        title: "Front Elevation Updated",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Failed to update front elevation:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update front elevation. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup image previews on unmount
  useEffect(() => {
    return () => {
      imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
    };
  }, []);

  if (!elevationData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-xl font-semibold">
          Loading front elevation details...
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto my-6 p-6 bg-white shadow-lg rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold">{elevationData.title}</h2>
          <p className="text-gray-600 mt-1">Elevation ID: {elevationData.id}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">
            Created: {new Date(elevationData.created_at).toLocaleDateString()}
          </p>
        </div>
        <button
          onClick={() => setShowEditModal(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
        >
          Edit Front Elevation
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="mb-2">
            <span className="font-semibold">Plot Dimension: </span>
            {elevationData.plot_dimension}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Covered Area: </span>
            {elevationData.covered_area}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Custom URL: </span>
            {elevationData.customUrl}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Floors: </span>
            {elevationData.floors}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Bedrooms: </span>
            {elevationData.bed_rooms}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Bathrooms: </span>
            {elevationData.bath_rooms}
          </p>
        </div>

        <div>
          <p className="mb-2">
            <span className="font-semibold">Car Parking: </span>
            {elevationData.car_parking ? "Yes" : "No"}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Terrace: </span>
            {elevationData.terrace ? "Yes" : "No"}
          </p>
          <p className="mb-2">
            <span className="font-semibold">Servant Quarters: </span>
            {elevationData.servant_quarters ? "Yes" : "No"}
          </p>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex border-b border-gray-300 mb-4">
          <button
            className={`py-2 px-4 font-medium focus:outline-none ${
              activeTab === "description"
                ? "border-b-2 border-blue-500 text-blue-500"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("description")}
          >
            Description
          </button>
          <button
            className={`py-2 px-4 font-medium focus:outline-none ${
              activeTab === "images"
                ? "border-b-2 border-blue-500 text-blue-500"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("images")}
          >
            Images
          </button>
        </div>

        {activeTab === "description" && (
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: elevationData.description }}
          />
        )}

        {activeTab === "images" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {elevationData.images?.map((img) => (
              <img
                key={img.id}
                src={img.image_url}
                alt="Front Elevation"
                className="rounded shadow-md w-full h-64 object-cover"
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-1/2 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Edit Front Elevation</h2>
            </div>
            <div className="overflow-y-auto flex-1">
              <form onSubmit={handleSubmit} className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom URL *
                    </label>
                    <input
                      type="text"
                      name="customUrl"
                      value={formData.customUrl}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                      placeholder="Enter custom URL"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plot Dimension
                    </label>
                    <input
                      type="text"
                      name="plot_dimension"
                      value={formData.plot_dimension}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Covered Area
                    </label>
                    <input
                      type="text"
                      name="covered_area"
                      value={formData.covered_area}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Floors
                    </label>
                    <input
                      type="number"
                      name="floors"
                      value={formData.floors}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bedrooms
                    </label>
                    <input
                      type="number"
                      name="bed_rooms"
                      value={formData.bed_rooms}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bathrooms
                    </label>
                    <input
                      type="number"
                      name="bath_rooms"
                      value={formData.bath_rooms}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Associate to category
                    </label>
                    <select
                      name="associated_elevation"
                      value={formData.associated_elevation}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md p-2"
                      required
                    >
                      <option value="">Select category</option>
                      <option value="10 to 20 Feet">10 to 20 Feet</option>
                      <option value="21 to 30 Feet">21 to 30 Feet</option>
                      <option value="31 to 40 Feet">31 to 40 Feet</option>
                      <option value="41 to 50 Feet">41 to 50 Feet</option>
                      <option value="51 to 65 Feet">51 to 65 Feet</option>
                      <option value="66 to 80 Feet">66 to 80 Feet</option>
                      <option value="others">Others</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <ReactQuill
                    value={formData.description}
                    onChange={handleQuillChange}
                    className="h-48 mb-12"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="car_parking"
                      checked={formData.car_parking}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Car Parking
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="terrace"
                      checked={formData.terrace}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Terrace
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="servant_quarters"
                      checked={formData.servant_quarters}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Servant Quarters
                    </label>
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Images
                  </label>

                  {/* Existing Images */}
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Current Images
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {existingImages.map((image, index) => (
                        <div key={image.id} className="relative group">
                          <img
                            src={image.image_url}
                            alt={`Front Elevation ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index, true)}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* New Images Preview */}
                  {imagePreviews.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">
                        New Images
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`New image ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Image Upload Button */}
                  <div className="mt-4">
                    <label className="flex items-center justify-center w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors">
                      <Upload size={20} className="mr-2" />
                      <span>Add New Images</span>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-2 border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 bg-blue-500 text-white rounded-md flex items-center ${
                      isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      "Update"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrontElevationDetails;
